import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig, useSimulation } from '../../contexts';
import {
  resolveNormalPrice,
  resolveSubscriberPrice,
  isHommePath,
  isDebugPricingEnabled,
  getFrequency,
  getHairLabels,
  getBrushingNormalPrice,
} from '../../utils/priceUtils';
import HeaderComponent from './header-component';
import SubHeaderComponent from './sub-header-component';
import StepHeader from './step-header';
import ChoiceGrid from './choice-grid';
import ChoiceCard from './choice-card';
import NavigationButton from './navigation-button';
import FrequencySelector from './frequency-selector';
import SummaryComponent from './summary-component';
import ProgressBar from './progress-bar';
import WizardModal from './wizard-modal';
import EmailModal from './email-modal';
import HelpModal from './help-modal';
import './wizard-container.css';

const WizardContainer = ({onBackToWelcome}) => {
  const navigate = useNavigate();
  const { config, steps } = useConfig();
  const {
    currentStep,
    setCurrentStep,
    selections,
    setSelections,
    history,
    setHistory,
    resetSimulation,
  } = useSimulation();

  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [modalSelectedChoice, setModalSelectedChoice] = useState(null);
  const [modalSelectedValue, setModalSelectedValue] = useState(null);
  const [nextStepAfterModal, setNextStepAfterModal] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [helpModalPath, setHelpModalPath] = useState('');

  const previousStepRef = useRef(null);
  
  const resetAllCardStates = () => {
    const allNonSelectedCards = document.querySelectorAll('.choice-card:not(.selected)');
    allNonSelectedCards.forEach(card => {
      card.classList.remove('hovered');
    });
  };
  
  const resetGroupCardStates = (groupName) => {
    const groupCards = document.querySelectorAll(`.choice-card[data-group="${groupName}"]`);
    groupCards.forEach(card => {
      card.classList.remove('hovered');
    });
  };
  
  useEffect(() => {
    if (previousStepRef.current !== currentStep) {
      resetAllCardStates();
      previousStepRef.current = currentStep;
    }
  }, [currentStep]);
  
  const handleHelpClick = (title, htmlPath) => {
    setHelpModalPath(htmlPath);
    setIsHelpModalOpen(true);
  };

  const handleSendEmail = () => {
    setIsEmailModalOpen(true);
  };

  const submitEmail = () => {
    // Le succès et les erreurs sont gérés et affichés directement dans EmailModal.
  };
  
  const currentStepConfig = useMemo(() => {
    return steps.find(step => step.name === currentStep);
  }, [steps, currentStep]);

  const choicesByGroup = useMemo(() => {
    if (!currentStepConfig || !currentStepConfig.choices) {
      return { default: [] };
    }
    
    const grouped = { default: [] };
    
    currentStepConfig.choices.forEach(choice => {
      if (choice.group) {
        if (!grouped[choice.group]) {
          grouped[choice.group] = [];
        }
        grouped[choice.group].push(choice);
      } else {
        grouped.default.push(choice);
      }
    });
    
    return grouped;
  }, [currentStepConfig]);

  const hasMultipleGroups = useMemo(() => {
    if (!currentStepConfig?.choices) return false;
    const groups = new Set();
    currentStepConfig.choices.forEach(choice => {
      if (choice.group) groups.add(choice.group);
    });
    return groups.size > 1;
  }, [currentStepConfig]);
  
  const selectedChoice = useMemo(() => {
    if (!selections[currentStep]) return null;
    
    if (hasMultipleGroups) {
      for (const groupKey in selections[currentStep]) {
        const groupSelection = selections[currentStep][groupKey];
        if (groupSelection?.choice?.select) {
          return groupSelection.choice;
        }
      }
      return null;
    } else {
      return selections[currentStep]?.choice;
    }
  }, [currentStep, selections, hasMultipleGroups]);

  const totalGroupedChoicesCount = useMemo(() => {
    if (!currentStepConfig || !currentStepConfig.choices) return 0;
    
    let totalCount = 0;
    Object.values(choicesByGroup).forEach(choices => {
      totalCount += choices.length;
    });
    
    return totalCount;
  }, [currentStepConfig, choicesByGroup]);
  
  const shouldUseSmallIcons = useMemo(() => {
    return totalGroupedChoicesCount > 4;
  }, [totalGroupedChoicesCount]);
  
  const selectedSelectValue = useMemo(() => {
    if (!selectedChoice) return null;
    return selectedChoice.selectedValue || 
           (selectedChoice.select ? Object.keys(selectedChoice.select.values)[0] : null);
  }, [selectedChoice]);
  
  const isFinalStep = currentStep === 'f-final';
  
  const lastRealStep = isFinalStep && history.length > 0 
    ? steps.find(step => step.name === history[history.length - 1])
    : null;
  
  const sexeValue = currentStepConfig?.sexe || lastRealStep?.sexe || 'femme';
  const wizardTitle = sexeValue === 'homme' 
    ? 'Vous souhaitez un Abo Addict HOMME' 
    : 'Vous souhaitez un Abo Addict FEMME';
  
  const wizardSubtitle = sexeValue === 'homme'
    ? 'pour prendre soin de votre chevelure et de votre barbe'
    : 'pour prendre soin de votre chevelure';
  
  const displayTitle = isFinalStep ? wizardTitle : currentStepConfig?.label || '';
  const displaySubtitle = isFinalStep ? wizardSubtitle : currentStepConfig?.sublabel || '';
  // La persistance est gérée par SimulationContext
  
  const handleChoiceSelect = (choice, index) => {
    if (choice.group) {
      resetGroupCardStates(choice.group);
    } else {
      resetAllCardStates();
    }
    
    setSelections(prev => {
      const updatedSelections = JSON.parse(JSON.stringify(prev));
      
      if (!updatedSelections[currentStep]) {
        updatedSelections[currentStep] = {};
      }
      
      if (hasMultipleGroups) {
        const groupKey = choice.group || 'default';
        
        updatedSelections[currentStep][groupKey] = {
          choiceIndex: index,
          choice: JSON.parse(JSON.stringify(choice)),
          timestamp: new Date().toISOString()
        };
        
      } else {
        updatedSelections[currentStep] = {
          choiceIndex: index,
          choice: JSON.parse(JSON.stringify(choice)),
          timestamp: new Date().toISOString()
        };
        
      }
      
      localStorage.setItem('selections', JSON.stringify(updatedSelections));
      
      return updatedSelections;
    });
    
    setTimeout(resetAllCardStates, 50);
  };
  
  const handleSelectChange = (choice, value) => {
    resetAllCardStates();
    
    setSelections(prev => {
      const currentSelection = prev[currentStep];
      
      if (hasMultipleGroups && currentSelection) {
        const groupKey = choice.group || 'default';
        const groupSelection = currentSelection[groupKey];
        
        if (groupSelection && groupSelection.choice.label === choice.label) {
          return {
            ...prev,
            [currentStep]: {
              ...currentSelection,
              [groupKey]: {
                ...groupSelection,
                choice: {
                  ...groupSelection.choice,
                  selectedValue: value
                }
              }
            }
          };
        }
      } else if (currentSelection && currentSelection.choice.label === choice.label) {
        return {
          ...prev,
          [currentStep]: {
            ...currentSelection,
            choice: {
              ...currentSelection.choice,
              selectedValue: value
            }
          }
        };
      }
      
      return prev;
    });
    
    const selectChangeEvent = new CustomEvent('frequency-select-change', {
      detail: { value }
    });
    document.dispatchEvent(selectChangeEvent);
  };
  
  const handleModalValidation = () => {
    if (modalSelectedChoice) {
      setShowModal(false);
      
      setSelections(prev => {
        const newSelections = {
          ...prev,
          [`modal-${currentStep}`]: {
            choiceIndex: 0,
            choice: {
              ...modalSelectedChoice,
              selectedValue: modalSelectedValue
            },
            timestamp: new Date().toISOString()
          }
        };
        return newSelections;
      });
      
      if (nextStepAfterModal) {
        setHistory(prev => [...prev, currentStep]);
        setCurrentStep(nextStepAfterModal);
      }
    }
  };

  const handleValidation = () => {
    if (!currentStepConfig || !selections[currentStep]) {
      return;
    }
    
    let nextStep;
    
    if (hasMultipleGroups) {
      const stepSelection = selections[currentStep];
      
      const groups = Object.keys(choicesByGroup).filter(g => g !== 'default');
      const allGroupsSelected = groups.every(group => {
        return stepSelection[group] && stepSelection[group].choice;
      });
      
      if (!allGroupsSelected) {
        alert("Veuillez faire un choix dans chaque catégorie");
        return;
      }
      
      const firstSelection = Object.values(stepSelection)[0];
      nextStep = firstSelection?.choice?.next;
    } else {
      const selectedChoice = selections[currentStep].choice;
      nextStep = selectedChoice?.next;
    }
    
    if (nextStep === "modal" && currentStepConfig.modal) {
      setModalConfig(currentStepConfig.modal);
      setModalSelectedChoice(null);
      setModalSelectedValue(null);
      
      const nextStepAfterModal = currentStepConfig.modal.choices[0]?.next || null;
      setNextStepAfterModal(nextStepAfterModal);
      
      setShowModal(true);
    } else if (nextStep) {
      setHistory(prev => [...prev, currentStep]);
      setCurrentStep(nextStep);
    }
  };
  
  const handleModalChoiceSelect = (choice, _index) => {
    resetAllCardStates();
    
    setModalSelectedChoice(choice);
    if (choice.select) {
      const defaultValue = Object.keys(choice.select.values)[0];
      setModalSelectedValue(defaultValue);
    } else {
      setModalSelectedValue(null);
    }
  };
  
  const handleModalSelectChange = (choice, value) => {
    setModalSelectedValue(value);
  };
  
  const handleBackNavigation = () => {
    if (history.length === 0) {
      resetSimulation();
      navigate('/');
    } else {
      const previousStep = history[history.length - 1];
      
      setSelections(prev => {
        const updatedSelections = { ...prev };
        
        delete updatedSelections[currentStep];
        const modalKey = `modal-${currentStep}`;
        if (updatedSelections[modalKey]) {
          delete updatedSelections[modalKey];
        }

        return updatedSelections;
      });
      
      const newHistory = history.slice(0, -1);
      setHistory(newHistory);
      
      setCurrentStep(previousStep);
    }
  };
  
  const handleResetSelection = () => {
    console.log('[Addict][Wizard] Reset demandé depuis l’étape :', currentStep);
    if (onBackToWelcome) {
      onBackToWelcome();
    } else {
      resetSimulation();
      navigate('/');
    }
  };
  
  
  const isDebugPricing = isDebugPricingEnabled();

  const getTotalPrice = () => {
    if (!selections) return null;
    
    let totalPrice = 0;
    // Fréquence du service principal (nécessaire pour multiplier le supplément Premium coloration)
    let mainServiceMultiplier = 1;
    if (selections['f-0']?.choice?.selectedValue) {
      mainServiceMultiplier = getFrequency(selections['f-0'].choice);
    }

    // Collecte des données debug par étape
    const debugSteps = isDebugPricing ? [] : null;

    // Si Premium est sélectionné en f-c-2, il remplace f-0 (ne pas cumuler les deux)
    const isColorationPremium = selections['f-c-2']?.choice?.label === 'Premium';
    
    Object.keys(selections).forEach(step => {
      const stepSelection = selections[step];
      
      if (typeof stepSelection === 'object' && stepSelection !== null) {
        if (stepSelection.choice && stepSelection.choice["subscriber-price"] !== undefined) {
          // Étape simple — résolution via matrice si disponible
          const choice = stepSelection.choice;

          // f-0 Coloration est remplacé par f-c-2 Premium : ne pas ajouter au total
          if (step === 'f-0' && isColorationPremium) {
            if (isDebugPricing) {
              const qty = choice.selectedValue ? parseInt(choice.selectedValue, 10) || 1 : 1;
              debugSteps.push({
                stepKey: step,
                items: [{
                  label: `${choice.label} ← remplacé par Premium`,
                  group: null,
                  subBasePrice: 0,
                  normBasePrice: 0,
                  qty,
                  finalSubPrice: 0,
                }],
              });
            }
            return;
          }

          const subBasePrice = resolveSubscriberPrice(choice, selections);
          let normBasePrice = isDebugPricing
            ? resolveNormalPrice(choice, selections)
            : 0;

          // Si le choix déclare "add-normal-brushing-price", on ajoute le prix
          // brushing (f-0/Brushing) résolu via sa matrice au prix normal
          if (isDebugPricing && choice["add-normal-brushing-price"]) {
            normBasePrice += getBrushingNormalPrice(selections, steps);
          }

          let qty = getFrequency(choice);

          // Le supplément Premium coloration est multiplié par la fréquence du service
          if (step === 'f-c-2' && choice.label === 'Premium') {
            qty *= mainServiceMultiplier;
          }

          const price = subBasePrice * qty;
          totalPrice += price;

          if (isDebugPricing) {
            debugSteps.push({
              stepKey: step,
              items: [{
                label: choice.label,
                group: null,
                subBasePrice,
                normBasePrice,
                qty,
                finalSubPrice: price,
              }],
            });
          }
        } else {
          // Étape groupée (ex : f-c-2 Classique/Premium si pas de subscriber-price)
          // Les steps longueur/épaisseur (f-b-1, f-c-1, f-g-1) n'ont plus de prix —
          // leur contribution est désormais intégrée dans la matrice de f-0.
          const groupItems = [];

          Object.values(stepSelection).forEach(groupSelection => {
            if (groupSelection && groupSelection.choice && groupSelection.choice["subscriber-price"] !== undefined) {
              const subBasePrice = resolveSubscriberPrice(groupSelection.choice, selections);
              const normBasePrice = isDebugPricing
                ? resolveNormalPrice(groupSelection.choice, selections)
                : 0;
              let qty = getFrequency(groupSelection.choice);

              const price = subBasePrice * qty;
              totalPrice += price;

              if (isDebugPricing) {
                groupItems.push({
                  label: groupSelection.choice.label,
                  group: groupSelection.choice.group || null,
                  subBasePrice,
                  normBasePrice,
                  qty,
                  finalSubPrice: price,
                });
              }
            }
          });

          if (isDebugPricing && groupItems.length > 0) {
            debugSteps.push({ stepKey: step, items: groupItems });
          }
        }
      }
    });
    
    if (isDebugPricing) {
      const fmt = (n) => n.toFixed(2).replace('.', ',');
      const allSubItems = [];
      const allNormItems = [];

      // ── Résumé du contexte de la simulation ──────────────────────────────
      const contextLines = [];

      if (isHommePath(selections)) {
        ['h-b-1', 'h-c-1', 'h-b-c-1', 'h-b-c-2'].forEach(s => {
          if (selections[s]?.choice?.label) {
            contextLines.push(`Service : ${selections[s].choice.label}`);
          }
        });
      } else {
        const serviceLabel = selections['f-0']?.choice?.label;
        if (serviceLabel) {
          const freq = getFrequency(selections['f-0'].choice);
          contextLines.push(`Service : ${serviceLabel} (${freq}×/mois)`);
        }

        const { lengthLabel, thicknessLabel } = getHairLabels(selections);
        if (lengthLabel)    contextLines.push(`Longueur  : ${lengthLabel}`);
        if (thicknessLabel) contextLines.push(`Épaisseur : ${thicknessLabel}`);
      }

      console.group(`[Addict][Wizard] Détail prix | Étape courante : ${currentStep}`);
      if (contextLines.length) {
        console.log('┌─ Contexte ─────────────────────────────');
        contextLines.forEach(l => console.log(`│  ${l}`));
        console.log('└────────────────────────────────────────');
      }

      debugSteps.forEach(({ stepKey, items }, stepIdx) => {
        console.log(`\n--- Étape ${stepIdx + 1} (${stepKey}) ---`);

        items.forEach(item => {
          const prefix = item.group ? `[${item.group}] ${item.label}` : item.label;
          console.log(`  ${prefix}`);
          console.log(`    Prix abonné : ${fmt(item.subBasePrice)} € (Qté ${item.qty})`);
          console.log(`    Prix normal : ${fmt(item.normBasePrice)} € (Qté ${item.qty})`);
          allSubItems.push(`${fmt(item.subBasePrice)} × ${item.qty}`);
          allNormItems.push(`${fmt(item.normBasePrice)} × ${item.qty}`);
        });

        const cumulativeSub = debugSteps
          .slice(0, stepIdx + 1)
          .flatMap(s => s.items)
          .reduce((sum, i) => sum + i.finalSubPrice, 0);

        const cumulativeNorm = debugSteps
          .slice(0, stepIdx + 1)
          .flatMap(s => s.items)
          .reduce((sum, i) => sum + i.normBasePrice * i.qty, 0);

        console.log(`\n  Total abonné : ${allSubItems.join(' + ')} = ${fmt(cumulativeSub)} €`);
        console.log(`  Total normal  : ${allNormItems.join(' + ')} = ${fmt(cumulativeNorm)} €`);
      });

      const totalNormal = debugSteps
        .flatMap(s => s.items)
        .reduce((sum, i) => sum + i.normBasePrice * i.qty, 0);

      console.log(`\n💰 TOTAL FINAL abonné : ${fmt(totalPrice)} €`);
      console.log(`💰 TOTAL FINAL normal  : ${fmt(totalNormal)} €`);
      console.groupEnd();
    }
    
    return totalPrice > 0 ? totalPrice.toFixed(2) : null;
  };
  
  const totalPrice = getTotalPrice();
  
  const canValidate = hasMultipleGroups
    ? selections[currentStep] && Object.keys(choicesByGroup).filter(g => g !== 'default').every(group => selections[currentStep][group]?.choice)
    : !!selections[currentStep];
  
  const shouldShowPrice = currentStepConfig?.recap && totalPrice !== null;
  
  if (!currentStepConfig && currentStep !== 'f-final') {
    return (
      <div className="wizard-error">
        <h2>Étape introuvable</h2>
        <p>L'étape {currentStep} n'existe pas dans la configuration.</p>
        <button onClick={() => setCurrentStep('h-1')}>Recommencer</button>
      </div>
    );
  }
  
  return (
    <div className="wizard-container">
      {/* En-tête principal avec boutons */}
      <HeaderComponent
        title="Je crée MON abonnement sans engagement"
        onBack={handleBackNavigation}
        onReset={handleResetSelection}
        showBackButton={true}
        showResetButton={true}
      />

      {/* Sous-en-tête */}
      <SubHeaderComponent title={wizardTitle} subtitle={wizardSubtitle} />

      {/* Barre de progression */}
      <ProgressBar
        currentStep={currentStep}
        selections={selections}
        configData={config}
      />

      {/* Titre de l'étape actuelle */}
      {!isFinalStep && (
        <StepHeader title={displayTitle} subtitle={displaySubtitle} />
      )}

      {/* Contenu principal */}
      <div className="wizard-content">
        {isFinalStep ? (
          <SummaryComponent
            selections={selections}
            onSendEmail={handleSendEmail}
            subscriberTotal={totalPrice}
          />
        ) : (
          <>
            {/* Afficher les choix par groupe */}
            {Object.entries(choicesByGroup).map(([groupName, choices]) => {
              if (choices.length === 0) return null;
              const groupTitle =
                groupName !== "default"
                  ? groupName === "longueur"
                    ? "Longueur de cheveux"
                    : groupName === "epaisseur"
                    ? "Épaisseur de cheveux"
                    : groupName
                  : null;

              return (
                <ChoiceGrid
                  key={groupName}
                  groupTitle={groupTitle}
                  totalChoicesCount={totalGroupedChoicesCount}
                >
                  {choices.map((choice, index) => {
                    let isSelected = false;

                    if (hasMultipleGroups) {
                      const groupKey = choice.group || "default";
                      isSelected =
                        selections[currentStep]?.[groupKey]?.choice?.label ===
                        choice.label;
                    } else {
                      isSelected =
                        selections[currentStep]?.choice?.label === choice.label;
                    }
                    return (
                      <ChoiceCard
                        key={`choice-${groupName}-${index}`}
                        choice={choice}
                        isSelected={isSelected}
                        onSelect={() => handleChoiceSelect(choice, index)}
                        onHelpClick={handleHelpClick}
                        reduced={shouldUseSmallIcons}
                      />
                    );
                  })}
                </ChoiceGrid>
              );
            })}

            {/* Sélecteur de fréquence */}
            {selectedChoice && selectedChoice.select && (
              <FrequencySelector
                selectedChoice={selectedChoice}
                onSelectChange={handleSelectChange}
                selectedValue={selectedSelectValue}
              />
            )}
          </>
        )}
      </div>

      {/* Spacer qui prend tout l'espace disponible */}
      <div className="flex-spacer"></div>

      {/* Pied de page contenant le bouton et le prix */}
      <div className="wizard-footer">
        {/* N'afficher le bouton de validation et le prix que si nous ne sommes pas à l'étape finale */}
        {currentStep !== "f-final" && (
          <>
            {/* Boutons de navigation */}
            <div className="wizard-navigation">
              <NavigationButton
                isEnabled={canValidate}
                onClick={handleValidation}
                label="Valider"
              />
            </div>

            {/* Affichage du prix de l'option sélectionnée uniquement si recap: true */}
            {shouldShowPrice && (
              <div className="price-display">
                <span className="price-label">
                  Soit un tarif unitaire des prestations abonnées de :
                </span>
                <span className="price-value">{totalPrice}€</span>
                <div className="price-bar" />
              </div>
            )}
          </>
        )}
      </div>
      <WizardModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        modalConfig={modalConfig}
        onSelect={handleModalChoiceSelect}
        onSelectChange={handleModalSelectChange}
        onValidate={handleModalValidation}
        selectedChoice={modalSelectedChoice}
        selectedValue={modalSelectedValue}
        onHelpClick={handleHelpClick}
      />
      {/* EmailModal */}
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSendEmail={submitEmail}
        selections={selections}
      />
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        helpPath={helpModalPath}
      />
    </div>
  );
};

export default WizardContainer;
